import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Section, SectionHeader, RowGroup, SaveButton } from "./_helpers";
import { Bell, MessageCircle, Trophy, Star, ShieldAlert, Newspaper, Send, Users, Handshake, Award } from "lucide-react";

// Kategorier: (icon, label, [kind-nøkler i backend rekkefølge])
const CATEGORIES = [
  {
    key: "social",
    icon: MessageCircle,
    title: "Sosialt",
    items: [
      { k: "follow", label: "Ny følger" },
      { k: "friend_request", label: "Ny venneforespørsel" },
      { k: "friend_accepted", label: "Venneforespørsel godtatt" },
      { k: "mention", label: "Du ble nevnt" },
      { k: "mention_accepted", label: "Skaper godtok å bli nevnt" },
      { k: "guestbook", label: "Nytt gjestebok-innlegg" },
    ],
  },
  {
    key: "messages",
    icon: Send,
    title: "Meldinger",
    items: [
      { k: "dm", label: "Direktemelding (DM)" },
      { k: "thread_reply", label: "Nytt svar i tråden" },
      { k: "creator_broadcast", label: "Massemelding fra skaper jeg følger" },
    ],
  },
  {
    key: "content",
    icon: Newspaper,
    title: "Innhold",
    items: [
      { k: "media_like", label: "Noen likte mitt bilde/video" },
      { k: "media_comment", label: "Noen kommenterte mitt bilde/video" },
      { k: "novelle_like", label: "Noen likte novellen min" },
      { k: "novelle_rate", label: "Ny vurdering av novellen min" },
      { k: "novelle_published", label: "Ny novelle fra skaper jeg følger" },
    ],
  },
  {
    key: "contests",
    icon: Trophy,
    title: "Konkurranser & lykkehjul",
    items: [
      { k: "contest_won", label: "Jeg vant en konkurranse" },
      { k: "contest_started", label: "Ny konkurranse startet" },
      { k: "contest_voting", label: "Avstemming åpen" },
      { k: "contest_entry_approved", label: "Mitt bidrag ble godkjent" },
      { k: "contest_entry_rejected", label: "Mitt bidrag ble avvist" },
      { k: "wheel_winner", label: "Lykkehjul-vinner" },
    ],
  },
  {
    key: "creator",
    icon: Star,
    title: "Skaper & selger",
    items: [
      { k: "creator_approved", label: "Skaper-søknad godkjent" },
      { k: "creator_rejected", label: "Skaper-søknad avslått" },
      { k: "creator_review", label: "Ny anmeldelse av meg som skaper" },
      { k: "seller_verification_reviewed", label: "Selger-verifisering behandlet" },
      { k: "content_sold", label: "Innholdet mitt ble kjøpt" },
      { k: "content_purchased", label: "Kjøps-kvittering" },
      { k: "social_sub_expiring", label: "Mitt sosiale abonnement snart utløpt" },
      { k: "social_sub_reminder", label: "Påminnelse om sosial abonnent" },
    ],
  },
  {
    key: "groups",
    icon: Users,
    title: "Grupper",
    items: [
      { k: "group_invite", label: "Ny gruppe-invitasjon" },
      { k: "group_join_request", label: "Ny søknad om gruppe-medlemskap" },
      { k: "group_post", label: "Nytt innlegg i gruppen" },
    ],
  },
  {
    key: "collab",
    icon: Handshake,
    title: "Samarbeid",
    items: [
      { k: "collab_request", label: "Ny samarbeids-forespørsel" },
      { k: "collab_accepted", label: "Samarbeid godtatt" },
    ],
  },
  {
    key: "trophy",
    icon: Award,
    title: "Trofé",
    items: [
      { k: "trophy", label: "Jeg oppnådde et nytt trofé" },
    ],
  },
  {
    key: "system",
    icon: ShieldAlert,
    title: "System",
    items: [
      { k: "moderation_action", label: "Moderering på min konto/innhold" },
      { k: "coaching_inquiry", label: "Ny coaching-forespørsel" },
      { k: "admin_message", label: "Melding fra administrasjonen" },
    ],
  },
];

// Radrender: label + 2 kolonner (App | Push)
function DoubleToggleRow({ label, appChecked, pushChecked, onApp, onPush, pushDisabled, testIdBase }) {
  return (
    <div
      className="flex items-center justify-between py-3.5 gap-4"
      data-testid={`${testIdBase}-row`}
    >
      <span className="font-manrope text-base text-[var(--cream)] min-w-0 flex-1">{label}</span>
      <div className="flex items-center gap-6 shrink-0">
        <label className="flex flex-col items-center gap-1 cursor-pointer" data-testid={`${testIdBase}-app`}>
          <input
            type="checkbox"
            checked={!!appChecked}
            onChange={(e) => onApp(e.target.checked)}
            className="h-5 w-5 accent-[var(--magenta)]"
          />
          <span className="font-manrope text-[10px] uppercase tracking-wider text-[var(--cream-soft)]">App</span>
        </label>
        <label
          className={`flex flex-col items-center gap-1 cursor-pointer ${pushDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
          data-testid={`${testIdBase}-push`}
        >
          <input
            type="checkbox"
            checked={!!pushChecked && !pushDisabled}
            onChange={(e) => onPush(e.target.checked)}
            disabled={pushDisabled}
            className="h-5 w-5 accent-[var(--magenta)]"
          />
          <span className="font-manrope text-[10px] uppercase tracking-wider text-[var(--cream-soft)]">Push</span>
        </label>
      </div>
    </div>
  );
}

export default function VarslerPage() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => { api.get("/users/me/notification-prefs").then(r => setPrefs(r.data)).catch(() => {}); }, []);

  const setBool = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/notification-prefs", prefs);
      setSavedAt(new Date());
      toast.success("Innstillinger lagret");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!prefs) return <div className="font-meta text-[var(--cream-soft)]">Laster…</div>;

  const pushMaster = prefs.push_enabled !== false;

  return (
    <>
      <PushSubscriptionSection />

      <Section testId="push-master-section">
        <SectionHeader
          icon={Bell}
          title="Push-varsler — hovedbryter"
          desc="Slår du denne av, sendes ingen push-varsler uansett hva du har valgt per varsel-type nedenfor. App-varsler (klokke-ikonet) påvirkes ikke."
        />
        <RowGroup>
          <label className="flex items-center justify-between py-4 cursor-pointer gap-6" data-testid="push-master-toggle-row">
            <span className="font-manrope text-base text-[var(--cream)]">Aktiver push-varsler</span>
            <input
              type="checkbox"
              checked={pushMaster}
              onChange={(e) => setBool("push_enabled", e.target.checked)}
              className="h-5 w-5 accent-[var(--magenta)]"
              data-testid="push-master-toggle"
            />
          </label>
        </RowGroup>
        <SaveButton onClick={save} saving={saving} testId="save-notif-prefs" />
        {savedAt && <span className="ml-3 font-meta text-emerald-300">✓ Lagret</span>}
      </Section>

      {CATEGORIES.map((cat) => (
        <Section key={cat.key} testId={`notif-cat-${cat.key}`}>
          <SectionHeader
            icon={cat.icon}
            title={cat.title}
            desc={
              <>
                <span className="font-manrope text-xs uppercase tracking-wider text-[var(--cream-soft)] mr-3">App</span>
                = klokke-ikonet oppe i header · &nbsp;
                <span className="font-manrope text-xs uppercase tracking-wider text-[var(--cream-soft)] mr-3">Push</span>
                = popup på låseskjerm/PC selv når du ikke er på siden
              </>
            }
          />
          <RowGroup>
            {cat.items.map((item) => (
              <DoubleToggleRow
                key={item.k}
                label={item.label}
                appChecked={prefs[item.k] !== false}
                pushChecked={prefs[`${item.k}_push`] !== false}
                onApp={(v) => setBool(item.k, v)}
                onPush={(v) => setBool(`${item.k}_push`, v)}
                pushDisabled={!pushMaster}
                testIdBase={`notif-${item.k}`}
              />
            ))}
          </RowGroup>
        </Section>
      ))}

      <Section testId="inbox-prefs-section">
        <SectionHeader title="Innboks-fanene" desc="Skjul spesifikke fane-innhold i Meldinger-siden uten å slå av varselet." />
        <RowGroup>
          <label className="flex items-center justify-between py-4 cursor-pointer gap-6" data-testid="inbox-inbox_guestbook-row">
            <span className="font-manrope text-base text-[var(--cream)]">Vis gjestebok-innlegg i innboksen</span>
            <input type="checkbox" checked={prefs.inbox_guestbook !== false}
              onChange={(e) => setBool("inbox_guestbook", e.target.checked)}
              className="h-5 w-5 accent-[var(--magenta)]" />
          </label>
          <label className="flex items-center justify-between py-4 cursor-pointer gap-6" data-testid="inbox-inbox_mention-row">
            <span className="font-manrope text-base text-[var(--cream)]">Vis nevnelser i innboksen</span>
            <input type="checkbox" checked={prefs.inbox_mention !== false}
              onChange={(e) => setBool("inbox_mention", e.target.checked)}
              className="h-5 w-5 accent-[var(--magenta)]" />
          </label>
        </RowGroup>
        <SaveButton onClick={save} saving={saving} testId="save-inbox-prefs" />
      </Section>
    </>
  );
}

// ============================================================
// PUSH-SUBSCRIPTION-SEKSJON (VAPID)
// ============================================================
function PushSubscriptionSection() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(ok);
    if (ok) {
      setPermission(Notification.permission);
      navigator.serviceWorker.ready.then((reg) =>
        reg.pushManager.getSubscription().then((sub) => setSubscribed(!!sub))
      ).catch(() => {});
    }
  }, []);

  const urlBase64ToUint8Array = (base64) => {
    const padding = "=".repeat((4 - (base64.length % 4)) % 4);
    const base64Pad = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64Pad);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
  };

  const subscribe = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Du må tillate varsler i nettleseren for å motta push.");
        return;
      }
      const { data: vapid } = await api.get("/push/vapid-public-key");
      if (!vapid.configured) {
        toast.error("Push-varsler er ikke konfigurert på server.");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid.public_key),
      });
      const subJson = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      toast.success("Push-varsler aktivert på denne enheten");
    } catch (e) {
      toast.error("Klarte ikke aktivere push: " + (e.message || "ukjent feil"));
    } finally { setBusy(false); }
  };

  const unsubscribe = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete(`/push/unsubscribe?endpoint=${encodeURIComponent(sub.endpoint)}`);
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Push-varsler deaktivert på denne enheten");
    } catch (e) {
      toast.error("Klarte ikke deaktivere push: " + (e.message || "ukjent feil"));
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/push/test");
      if (data.ok) {
        toast.success(`Test-push sendt til ${data.count} enhet${data.count === 1 ? "" : "er"}. Sjekk låseskjerm/varsler.`);
      } else {
        toast.error("Ingen aktive push-abonnement funnet. Aktiver først.");
      }
    } catch (e) {
      toast.error("Klarte ikke sende test-push: " + formatApiError(e));
    } finally { setTesting(false); }
  };

  return (
    <Section testId="push-section">
      <SectionHeader
        icon={Bell}
        title="Push på denne enheten"
        desc="Push-varsler sendes til denne enheten (nettleser eller PWA på hjemskjerm) selv når du ikke er på norskerotikk.no. Fungerer på Chrome/Edge/Firefox på PC og Android. iOS krever iOS 16.4+ og at siden er installert på hjemskjerm."
      />
      {!supported ? (
        <p className="font-fraunces italic text-[var(--cream-soft)]">Nettleseren din støtter ikke push-varsler.</p>
      ) : permission === "denied" ? (
        <p className="font-fraunces italic text-[var(--magenta)]">
          Du har blokkert varsler for norskerotikk.no. Åpne nettleser-innstillingene og tillat varsler for å bruke push.
        </p>
      ) : subscribed ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-fraunces italic text-emerald-300">✓ Push-varsler er aktive på denne enheten</span>
          <button onClick={sendTest} disabled={testing} className="btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="push-test-btn">
            <Bell className="h-3.5 w-3.5" /> {testing ? "Sender…" : "Send test-push"}
          </button>
          <button onClick={unsubscribe} disabled={busy} className="btn-ghost text-xs" data-testid="push-unsubscribe">
            {busy ? "Skrur av…" : "Skru av på denne enheten"}
          </button>
        </div>
      ) : (
        <button onClick={subscribe} disabled={busy}
          className="btn-magenta inline-flex items-center gap-1.5 text-xs" data-testid="push-subscribe">
          <Bell className="h-3.5 w-3.5" /> {busy ? "Aktiverer…" : "Aktiver push-varsler"}
        </button>
      )}
    </Section>
  );
}
